import React, { useState, Fragment, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Disclosure, Popover, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  ShoppingBagIcon,
  XMarkIcon,
  UserIcon,
  MagnifyingGlassIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import CartSidebar from '../cart/CartSidebar';
import QuickSearch from '../search/QuickSearch';
import NotificationBell from '../notifications/NotificationBell';
import MegaMenu from '../categories/MegaMenu';
import axios from 'axios';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isAdmin, isCompanyUser } = useAuth();
  const { getItemCount, toggleCart } = useCart();

  // Fetch wishlist count
  useEffect(() => {
    const fetchWishlistCount = async () => {
      if (user) {
        try {
          const response = await axios.get('/api/wishlist/count');
          setWishlistCount(response.data.count);
        } catch (error) {
          console.error('Error fetching wishlist count:', error);
          setWishlistCount(0);
        }
      } else {
        setWishlistCount(0);
      }
    };

    fetchWishlistCount();
  }, [user]);

  const navigation = [
    { name: 'בית', href: '/' },
    { name: 'קטגוריות', href: '/categories', hasMegaMenu: true },
    { name: 'מוצרים', href: '/products' },
    { name: 'חבילות מבצע', href: '/bundles' },
    { name: 'אודות', href: '/about' },
    { name: 'צור קשר', href: '/contact' },
  ];

  const userNavigation = [
    { name: 'הפרופיל שלי', href: '/account' },
    { name: 'הגדרות פרופיל', href: '/profile' },
    { name: 'היסטוריית הזמנות', href: '/account/order-history' },
    { name: 'תבניות הזמנה', href: '/account/order-templates' },
    { name: 'רשימת משאלות', href: '/wishlist' },
  ];

  // B2B specific navigation
  const b2bNavigation = [
    { name: 'פורטל B2B', href: '/trade' },
    { name: 'משא ומתן על מחירים', href: '/price-negotiations' },
    { name: 'היסטוריית הזמנות', href: '/account/order-history' },
    { name: 'תבניות הזמנה', href: '/account/order-templates' },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-50" role="banner">
        <nav id="main-navigation" className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8" aria-label="תפריט ניווט ראשי" role="navigation">
          {/* Mobile menu button - Touch optimized */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg p-2.5 sm:p-2 text-gray-700 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="פתח תפריט ראשי"
            >
              <Bars3Icon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
            </button>
          </div>

          {/* Logo - Responsive */}
          <div className="flex lg:flex-1">
            <Link to="/" className="p-1.5 sm:p-1 flex items-center">
              <span className="sr-only">LUMEL Elegant Essence</span>
              <img 
                src="/lumel-logo.jpeg" 
                alt="LUMEL Elegant Essence" 
                className="h-8 w-auto sm:h-10"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden lg:flex lg:gap-x-12 relative">
            {navigation.map((item) => (
              item.hasMegaMenu ? (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setMegaMenuOpen(true)}
                  onMouseLeave={() => setMegaMenuOpen(false)}
                >
                  <Link
                    to={item.href}
                    className={classNames(
                      location.pathname === item.href
                        ? 'text-primary-600 font-semibold'
                        : 'text-gray-900 hover:text-primary-600',
                      'text-sm font-medium transition-colors duration-200 flex items-center'
                    )}
                  >
                    {item.name}
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                  </Link>
                  <MegaMenu 
                    isOpen={megaMenuOpen} 
                    onClose={() => setMegaMenuOpen(false)} 
                  />
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={classNames(
                    location.pathname === item.href
                      ? 'text-primary-600 font-semibold'
                      : 'text-gray-900 hover:text-primary-600',
                    'text-sm font-medium transition-colors duration-200'
                  )}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* Quick Search */}
          <div className="hidden md:flex flex-1 max-w-lg mx-8">
            <QuickSearch className="w-full" />
          </div>

          {/* Right side buttons - Mobile optimized */}
          <div className="flex items-center gap-x-2 sm:gap-x-4 lg:gap-x-6">
            {/* Notifications - Hide on very small screens */}
            <div className="hidden xs:block">
              <NotificationBell />
            </div>

            {/* Cart - Touch optimized */}
            <button
              type="button"
              className="relative p-2.5 sm:p-2 text-gray-400 hover:text-gray-500 active:text-gray-600 touch-manipulation rounded-lg hover:bg-gray-100"
              onClick={toggleCart}
              aria-label="עגלת קניות"
            >
              <ShoppingBagIcon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
              {getItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 sm:-top-0.5 sm:-right-0.5 h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-primary-600 flex items-center justify-center text-xs font-bold text-white">
                  {getItemCount()}
                </span>
              )}
            </button>

            {/* Wishlist - Show on mobile too */}
            <Link
              to="/wishlist"
              className="relative p-2.5 sm:p-2 text-gray-400 hover:text-gray-500 active:text-gray-600 touch-manipulation rounded-lg hover:bg-gray-100"
              aria-label="רשימת משאלות"
            >
              <HeartIcon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 sm:-top-0.5 sm:-right-0.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* User menu - Mobile optimized */}
            {isAuthenticated ? (
              <Popover className="relative">
                <Popover.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 hover:text-primary-600 p-2 rounded-lg hover:bg-gray-100 touch-manipulation">
                  <UserIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">{user?.first_name}</span>
                  <ChevronDownIcon className="h-5 w-5 flex-none text-gray-400 hidden sm:block" aria-hidden="true" />
                </Popover.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel className="absolute left-1/2 z-10 mt-5 flex w-screen max-w-max -translate-x-1/2 px-4">
                    <div className="w-screen max-w-sm flex-auto rounded-3xl bg-white p-4 text-sm leading-6 shadow-lg ring-1 ring-gray-900/5">
                      <div className="p-2 border-b border-gray-200">
                        <p className="font-semibold text-gray-900">{user?.first_name} {user?.last_name}</p>
                        <p className="text-gray-600">{user?.email}</p>
                        {user?.company && (
                          <p className="text-sm text-gray-500">{user.company.name}</p>
                        )}
                      </div>
                      
                      <div className="py-2">
                        {userNavigation.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="block rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-50"
                          >
                            {item.name}
                          </Link>
                        ))}
                        
                        {(user?.role === 'b2b' || user?.role === 'seller' || user?.role === 'admin') && (
                          <Link
                            to="/trade"
                            className="block rounded-lg px-3 py-2 text-blue-700 hover:bg-blue-50 font-medium"
                          >
                            🏢 קטלוג עסקי
                          </Link>
                        )}
                        
                        {(user?.role === 'b2b' || user?.role === 'seller') && user?.company_id && (
                          <Link
                            to="/price-negotiations"
                            className="block rounded-lg px-3 py-2 text-purple-700 hover:bg-purple-50 font-medium"
                          >
                            💬 משא ומתן על מחירים
                          </Link>
                        )}
                        
                        {user?.role === 'admin' && (
                          <>
                            <Link
                              to="/admin"
                              className="block rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-50"
                            >
                              ⚙️ ממשק ניהול
                            </Link>
                            <Link
                              to="/admin/price-negotiations"
                              className="block rounded-lg px-3 py-2 text-purple-700 hover:bg-purple-50"
                            >
                              💬 משא ומתן (ניהול)
                            </Link>
                          </>
                        )}
                        
                        <button
                          onClick={handleLogout}
                          className="block w-full text-right rounded-lg px-3 py-2 text-gray-900 hover:bg-gray-50"
                        >
                          התנתק
                        </button>
                      </div>
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            ) : (
              <div className="flex gap-x-2 sm:gap-x-4">
                <Link
                  to="/login"
                  className="text-sm sm:text-base font-semibold px-3 py-2 text-gray-900 hover:text-primary-600 hover:bg-gray-100 rounded-lg touch-manipulation"
                >
                  התחבר
                </Link>
                <Link
                  to="/register"
                  className="btn btn-primary text-xs sm:text-sm px-3 sm:px-4 py-2 touch-manipulation"
                >
                  הרשם
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile menu - Optimized */}
        <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-50 bg-black/30" />
          <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-5 py-5 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between mb-6">
              <Link to="/" className="flex items-center p-1.5" onClick={() => setMobileMenuOpen(false)}>
                <img 
                  src="/lumel-logo.jpeg" 
                  alt="LUMEL Elegant Essence" 
                  className="h-10 w-auto"
                />
              </Link>
              <button
                type="button"
                className="rounded-lg p-2.5 text-gray-700 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="סגור תפריט"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            
            <div className="flow-root">
              <div className="space-y-4">
                {/* Mobile search - Touch optimized */}
                <div>
                  <form onSubmit={handleSearch}>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-4 py-3 text-base border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="חפש מוצרים..."
                      />
                    </div>
                  </form>
                </div>

                {/* Navigation */}
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* User actions */}
                <div className="py-6">
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <div className="px-3 py-2 border-b border-gray-200">
                        <p className="font-semibold text-gray-900">{user?.first_name} {user?.last_name}</p>
                        <p className="text-sm text-gray-600">{user?.email}</p>
                      </div>
                      
                      {userNavigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                      
                      {(isAdmin || isCompanyUser) && (
                        <Link
                          to="/admin"
                          className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          ממשק ניהול
                        </Link>
                      )}
                      
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="-mx-3 block w-full text-right rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                      >
                        התנתק
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link
                        to="/login"
                        className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        התחבר
                      </Link>
                      <Link
                        to="/register"
                        className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        הרשם
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </Dialog>
      </header>

      {/* Cart Sidebar */}
      <CartSidebar />
    </>
  );
};

export default Navbar;
